import { apiFetch } from "./client";

export interface SkillCatalogueItem {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  icon: string | null;
  contentMd: string | null;
  requiredMcps: string[] | null;
  parametersSchema: Record<string, unknown> | null;
  skillType: string;
  version: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface DiscoveredSkill {
  id: string;
  agentId: string;
  companyId: string;
  name: string;
  description: string | null;
  contentMd: string | null;
  dirName: string;
  status: "discovered" | "promoted" | "removed";
  promotedToTenantSkillId: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
}

/**
 * One row of `GET /skills/agents/:agentId` — the assignment plus the
 * joined display fields the slash menu needs (skill name, plus the tenant
 * skill's own enabled flag so disabling at the tenant directory level
 * removes it from every agent's slash menu without touching assignments).
 *
 * Globals (`scope === "global"`) appear in this list even when the agent
 * has no explicit assignment — the server synthesises a virtual row with
 * `id` = `global:<tenantSkillId>`, `assignedAt = null`, and
 * `parameterOverrides = null`. Treat `id` starting with `global:` as a
 * non-deletable virtual assignment in the UI.
 */
export interface AgentSkillRow {
  id: string;
  agentId: string;
  tenantSkillId: string;
  enabled: boolean;
  parameterOverrides: Record<string, unknown> | null;
  assignedAt: string | null;
  name: string;
  tenantEnabled: boolean;
  scope: "agent" | "global";
  catalogueDescription: string | null;
  catalogueDisplayName: string | null;
}

export type TenantSkillScope = "agent" | "global" | "workflow";

/**
 * A row of `GET /skills/tenants/:companyId`.
 *
 * `scope: "workflow"` rows are procedures for workflow agent nodes. They are
 * never loaded into an agent's chat prompt and never appear in
 * `listAgentSkills` — the only place they surface is the workflow canvas.
 */
export interface TenantSkillRow {
  id: string;
  companyId: string;
  catalogueSkillId: string | null;
  name: string;
  customContentMd: string | null;
  parameterValues: Record<string, unknown> | null;
  enabled: boolean;
  scope: TenantSkillScope;
  createdAt: string;
  updatedAt: string;
}

export interface SkillDiscoveryResult {
  discovered: Array<{ name: string; dirName: string; description: string | null; contentMd: string }>;
  platformManaged: string[];
  removed: string[];
  error?: string;
}

export const skillsApi = {
  // Catalogue
  listCatalogue: () => apiFetch<SkillCatalogueItem[]>("/skills/catalogue"),
  getCatalogue: (id: string) => apiFetch<SkillCatalogueItem>(`/skills/catalogue/${id}`),
  createCatalogue: (data: Record<string, unknown>) =>
    apiFetch<SkillCatalogueItem>("/skills/catalogue", { method: "POST", body: JSON.stringify(data) }),
  updateCatalogue: (id: string, data: Record<string, unknown>) =>
    apiFetch<SkillCatalogueItem>(`/skills/catalogue/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteCatalogue: (id: string) =>
    apiFetch<void>(`/skills/catalogue/${id}`, { method: "DELETE" }),
  generateCatalogue: (description: string) =>
    apiFetch<SkillCatalogueItem>("/skills/catalogue/generate", {
      method: "POST",
      body: JSON.stringify({ description }),
    }),

  // Tenant Skills
  listTenantSkills: (companyId: string) => apiFetch<unknown[]>(`/skills/tenants/${companyId}`),
  /**
   * Scope-filtered tenant skills. The workflow canvas passes
   * `scope: "workflow"` so the node's procedure picker offers only skills
   * that can actually be attached to a step.
   */
  listTenantSkillsByScope: (companyId: string, scope: TenantSkillScope) =>
    apiFetch<TenantSkillRow[]>(
      `/skills/tenants/${companyId}?scope=${encodeURIComponent(scope)}`,
    ),
  assignTenantSkill: (companyId: string, data: Record<string, unknown>) =>
    apiFetch<unknown>(`/skills/tenants/${companyId}`, { method: "POST", body: JSON.stringify(data) }),
  /**
   * PATCH a tenant skill. Used for flipping scope between "agent" (default —
   * only assigned agents load it), "global" (every agent in the company
   * loads it) and "workflow" (no agent loads it; attached to workflow nodes
   * and delivered per-step). The server fans out syncSkills to every agent
   * in the tenant on any scope change — including into and out of
   * "workflow", which is how the file gets removed from live VMs.
   */
  updateTenantSkill: (
    companyId: string,
    id: string,
    data: { scope?: TenantSkillScope; enabled?: boolean; name?: string },
  ) =>
    apiFetch<unknown>(`/skills/tenants/${companyId}/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  removeTenantSkill: (companyId: string, id: string) =>
    apiFetch<void>(`/skills/tenants/${companyId}/${id}`, { method: "DELETE" }),

  // Agent Skills
  listAgentSkills: (agentId: string) =>
    apiFetch<AgentSkillRow[]>(`/skills/agents/${agentId}`),
  assignAgentSkill: (agentId: string, data: Record<string, unknown>) =>
    apiFetch<unknown>(`/skills/agents/${agentId}`, { method: "POST", body: JSON.stringify(data) }),
  removeAgentSkill: (agentId: string, id: string) =>
    apiFetch<void>(`/skills/agents/${agentId}/${id}`, { method: "DELETE" }),

  /**
   * Upload a SKILL.md from the chat input. Creates a tenant-scoped skill
   * (no platform catalogue entry) and assigns it to the agent. The agent's
   * workspace is then synced so the skill is loadable on the next turn.
   */
  uploadAgentSkill: (
    agentId: string,
    data: { name?: string; description?: string; content: string },
  ) =>
    apiFetch<{
      tenantSkillId: string;
      agentSkillId: string;
      name: string;
      created: boolean;
    }>(`/skills/agents/${agentId}/upload`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // Discovery — read skills from agent VM
  discoverFromAgent: (agentId: string) =>
    apiFetch<SkillDiscoveryResult>(`/agents/${agentId}/discover-skills`, { method: "POST" }),
  listDiscovered: (agentId: string) =>
    apiFetch<DiscoveredSkill[]>(`/agents/${agentId}/discovered-skills`),
  removeDiscovered: (agentId: string, id: string) =>
    apiFetch<void>(`/agents/${agentId}/discovered-skills/${id}`, { method: "DELETE" }),
  promoteDiscovered: (agentId: string, id: string, data?: { targetAgentIds?: string[] }) =>
    apiFetch<{ success: boolean; tenantSkillId?: string }>(`/agents/${agentId}/discovered-skills/${id}/promote`, {
      method: "POST",
      body: JSON.stringify(data || {}),
    }),
};
